import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  LogOut, 
  CheckCircle2, 
  Circle, 
  ChevronRight, 
  Sparkles,
  Target,
  Hammer,
  Rocket,
  Lock,
  BookOpen,
  Award,
  Database
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEMO_USER_EMAIL = "demo@prayertoprototype.com";

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState([]);
  const [currentDay, setCurrentDay] = useState(1);
  const [completedDays, setCompletedDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [startingJourney, setStartingJourney] = useState(false);
  const [day1Data, setDay1Data] = useState(null);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [loadingDemoData, setLoadingDemoData] = useState(false);

  const isDemoUser = user?.email === DEMO_USER_EMAIL;

  const dayConfig = [
    { day: 1, title: "Pain Point Matrix", phase: "Clarity", icon: Sparkles, time: "45 min", description: "Discover the one problem you are built to solve", free: true },
    { day: 2, title: "Your Unfair Advantage", phase: "Clarity", icon: BookOpen, time: "60 min", description: "Find out why you are the right person to build this" },
    { day: 3, title: "One Committed Idea", phase: "Validation", icon: Target, time: "60 min", description: "Choose one ranked business idea to build" },
    { day: 4, title: "A Scoped MVP", phase: "Validation", icon: Target, time: "60 min", description: "Define exactly what version one looks like" },
    { day: 5, title: "Landing Page Copy", phase: "Build", icon: Hammer, time: "60 min", description: "Write copy in your voice, from your story" },
    { day: 6, title: "A Live Landing Page", phase: "Build", icon: Hammer, time: "90 min", description: "Build and preview your page, no coding needed" },
    { day: 7, title: "A Real Decision", phase: "Launch", icon: Rocket, time: "60 min", description: "GO, PIVOT, or RELEASE with evidence" },
  ];

  useEffect(() => {
    fetchProgress();
    // Check if returning user
    const lastVisit = localStorage.getItem('ptp_last_visit');
    if (lastVisit) {
      const hoursSinceVisit = (Date.now() - parseInt(lastVisit)) / (1000 * 60 * 60);
      if (hoursSinceVisit > 1) {
        setShowWelcomeBack(true);
      }
    }
    localStorage.setItem('ptp_last_visit', Date.now().toString());
  }, []);

  useEffect(() => {
    if (showWelcomeBack && !loading) {
      toast.success("Welcome back. Your work is saved.", {
        duration: 3000
      });
      setShowWelcomeBack(false);
    }
  }, [showWelcomeBack, loading]);

  const fetchProgress = async () => {
    try {
      const [progressResponse, day1Response] = await Promise.all([
        axios.get(`${API}/journey/progress`, { withCredentials: true }),
        axios.get(`${API}/journey/day/1`, { withCredentials: true }).catch(() => ({ data: { form_data: null } }))
      ]);
      
      setProgress(progressResponse.data.progress);
      setCurrentDay(progressResponse.data.current_day);
      setCompletedDays(progressResponse.data.completed_days);
      setDay1Data(day1Response.data.form_data);
      
      if (setUser && progressResponse.data.user) {
        setUser(progressResponse.data.user);
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
      toast.error("Failed to load progress");
    } finally {
      setLoading(false);
    }
  };

  const handleStartJourney = async () => {
    // Day 1 is free - allow anyone to start
    try {
      setStartingJourney(true);
      await axios.post(`${API}/journey/start`, {}, { withCredentials: true });
      toast.success("Your journey has begun!");
      fetchProgress();
      navigate('/day/1');
    } catch (error) {
      console.error('Error starting journey:', error);
      toast.error("Failed to start journey");
    } finally {
      setStartingJourney(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const handleLoadDemoData = async () => {
    if (!isDemoUser) return;
    
    setLoadingDemoData(true);
    try {
      await axios.post(`${API}/admin/load-demo-data`, {}, { withCredentials: true });
      toast.success("Demo data loaded! Redirecting to Day 7...");
      setTimeout(() => {
        navigate('/day/7');
      }, 1000);
    } catch (error) {
      console.error('Error loading demo data:', error);
      toast.error("Failed to load demo data");
    } finally {
      setLoadingDemoData(false);
    }
  };

  const handleDayClick = (day) => {
    // Day 1 is always accessible (free)
    if (day === 1) {
      if (!user.journey_started) {
        handleStartJourney();
        return;
      }
      navigate('/day/1');
      return;
    }
    
    // Days 2-7 require payment
    if (!user.has_paid) {
      // If Day 1 is complete, show the gate
      const day1Progress = progress.find(p => p.day === 1);
      if (day1Progress?.completed) {
        navigate('/day1-complete');
      } else {
        toast.error("Complete Day 1 first (it's free!)");
      }
      return;
    }
    
    if (!user.journey_started) {
      handleStartJourney();
      return;
    }
    
    const dayProgress = progress.find(p => p.day === day);
    if (day > currentDay && !dayProgress?.completed) {
      toast.error("Complete previous days first");
      return;
    }
    
    navigate(`/day/${day}`);
  };

  const progressPercent = (completedDays / 7) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#d4af37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-[#151515]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#d4af37]/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#d4af37]" />
            </div>
            <span className="text-[#d4af37] font-medium">Prayer</span>
            <span className="text-white/70 font-medium"> to </span>
            <span className="text-[#d4af37] font-medium">Prototype</span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-medium">{user.name}</p>
              <p className="text-zinc-500 text-xs">{user.email}</p>
            </div>
            {user.picture && (
              <img 
                src={user.picture} 
                alt={user.name}
                className="w-10 h-10 rounded-full border-2 border-[#d4af37]/30"
              />
            )}
            {user.is_founding_member && (
              <div className="hidden sm:flex items-center gap-1 bg-[#d4af37]/10 text-[#d4af37] px-3 py-1 rounded-full text-xs font-medium">
                <Award className="w-3 h-3" />
                Founding Member
              </div>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Demo User Admin Button */}
        {isDemoUser && (
          <Card 
            className="mb-8 p-4 border-dashed"
            style={{
              background: 'rgba(212,175,55,0.05)',
              border: '2px dashed rgba(212,175,55,0.3)'
            }}
            data-testid="demo-admin-card"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Database className="w-5 h-5 text-[#d4af37]" />
                <div>
                  <p className="text-white font-medium text-sm">Demo Account</p>
                  <p className="text-white/50 text-xs">Load pre-filled data to test the full flow</p>
                </div>
              </div>
              <Button
                onClick={handleLoadDemoData}
                disabled={loadingDemoData}
                className="bg-[#d4af37] text-black hover:bg-[#e8c84a]"
                data-testid="load-demo-data-btn"
              >
                {loadingDemoData ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Loading...
                  </span>
                ) : (
                  "Load Demo Data"
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-light text-white mb-2">
            Welcome, <span className="text-[#d4af37]">{user.name?.split(' ')[0]}</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            {user.journey_started 
              ? completedDays === 7 
                ? "You've completed your journey! Review your work below."
                : "Continue your 7 day sprint to your prototype."
              : "Ready to go from prayer to prototype in 7 days?"
            }
          </p>
        </div>

        {/* Progress Overview */}
        {user.journey_started && (
          <>
            {/* Day 1 Summary Card - shows on Day 2+ if Day 1 data exists */}
            {currentDay >= 2 && day1Data && (day1Data.core_opportunity || day1Data.pain_matrix?.some(p => p.pain?.trim())) && (
              <Card 
                className="mb-6 p-5"
                style={{
                  background: 'rgba(212,175,55,0.05)',
                  border: '1px solid #d4af37',
                  borderRadius: '10px'
                }}
                data-testid="day1-summary-card"
              >
                <p 
                  className="text-[#d4af37] uppercase text-[0.7rem] font-medium mb-3"
                  style={{ letterSpacing: '1.5px' }}
                >
                  CARRYING FORWARD FROM DAY 1
                </p>
                {day1Data.core_opportunity?.winning_pain_point && (
                  <p className="text-white font-bold mb-2">
                    {day1Data.core_opportunity.winning_pain_point}
                  </p>
                )}
                {!day1Data.core_opportunity?.winning_pain_point && day1Data.pain_matrix && (
                  <p className="text-white font-bold mb-2">
                    {day1Data.pain_matrix.find(p => p.pain?.trim())?.pain || 'Your top pain point'}
                  </p>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                  {day1Data.core_opportunity && (
                    <span 
                      className="inline-block px-3 py-1 rounded-full text-sm"
                      style={{
                        background: 'rgba(212,175,55,0.2)',
                        color: '#d4af37'
                      }}
                    >
                      {(() => {
                        const filledPains = day1Data.pain_matrix?.filter(p => p.pain?.trim()).length || 0;
                        if (filledPains >= 10) return "The Visionary Builder";
                        if (filledPains >= 7) return "The Faithful Overthinker";
                        if (filledPains >= 4) return "The Ready Founder";
                        return "The Emerging Dreamer";
                      })()}
                    </span>
                  )}
                </div>
                {day1Data.core_opportunity?.unique_angle && (
                  <p className="text-white/70 italic mt-3 text-sm">
                    {day1Data.core_opportunity.unique_angle}
                  </p>
                )}
              </Card>
            )}
            
            <Card className="bg-[#1f1f1f] border-zinc-800 p-6 mb-12" data-testid="progress-card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-medium">Your Progress</h2>
                <span className="text-[#d4af37] font-medium">{completedDays}/7 Days</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-zinc-800" />
              <div className="flex items-center justify-between mt-4">
                <p className="text-zinc-500 text-sm">
                  {completedDays === 7 
                    ? "Congratulations! You've completed your journey!"
                    : `Day ${currentDay}: ${dayConfig[currentDay - 1]?.title}`
                  }
                </p>
                {completedDays === 7 && (
                  <div className="flex items-center gap-2 text-[#d4af37] text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    Journey Complete
                  </div>
                )}
              </div>
            </Card>
          </>
        )}

        {/* Onboarding / Start Journey CTA */}
        {!user.journey_started && (
          <Card className="bg-[#1f1f1f] border-[#d4af37]/20 p-8 mb-12" data-testid="start-journey-card">
            <div className="max-w-2xl mx-auto text-center">
              <Sparkles className="w-12 h-12 text-[#d4af37] mx-auto mb-6" />
              <h2 className="text-2xl font-light text-white mb-4">
                Welcome to Prayer to Prototype
              </h2>
              <p className="text-zinc-400 mb-6 leading-relaxed">
                Over the next 7 days, you'll move from idea to live prototype. Each day includes 
                Scripture, prayer, exercises, and reflection time. This isn't rushed. Take the time you need.
              </p>
              
              <div className="bg-[#151515] rounded-xl p-6 mb-8 text-left">
                <h3 className="text-[#d4af37] font-medium mb-4">Before you start:</h3>
                <ul className="space-y-3 text-zinc-400">
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#d4af37]"></div>
                    Block 1 to 2 hours daily
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#d4af37]"></div>
                    Have a notebook ready (digital or physical)
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#d4af37]"></div>
                    Access to ChatGPT or Claude for AI prompts
                  </li>
                  <li className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#d4af37]"></div>
                    Commit to finishing all 7 days
                  </li>
                </ul>
              </div>

              <div className="bg-[#151515] rounded-xl p-6 mb-8 border border-[#d4af37]/20">
                <p className="text-zinc-300 italic">
                  "Lord, thank You for this idea You've placed in me. Give me courage to move forward 
                  and wisdom to build with excellence. Order my steps. In Jesus' name, Amen."
                </p>
              </div>

              <Button 
                onClick={handleStartJourney}
                disabled={startingJourney}
                className="btn-gold rounded-full px-10 py-6 text-lg gold-glow"
                data-testid="start-journey-btn"
              >
                {startingJourney ? (
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Loading...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Begin Day 1 <ChevronRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </div>
          </Card>
        )}

        {/* Journey Days Grid */}
        <div className="mb-8">
          <h2 className="text-xl font-medium text-white mb-6">Your 7 Day Journey</h2>
          
          <div className="space-y-4">
            {dayConfig.map((config) => {
              const dayProgress = progress.find(p => p.day === config.day);
              const isCompleted = dayProgress?.completed;
              const isCurrent = config.day === currentDay && !isCompleted && user.journey_started;
              // Day 1 is never locked, Days 2-7 require payment
              const isLocked = config.day === 1 
                ? false 
                : !user.has_paid || !user.journey_started || (config.day > currentDay && !isCompleted);
              
              return (
                <Card 
                  key={config.day}
                  onClick={() => handleDayClick(config.day)}
                  className={`
                    p-6 cursor-pointer transition-all duration-300 flex items-center gap-6
                    ${isCompleted ? 'bg-[#1f1f1f] border-[#d4af37]/30' : 'bg-[#1f1f1f] border-zinc-800'}
                    ${isCurrent ? 'border-[#d4af37] shadow-[0_0_30px_rgba(212,175,55,0.1)]' : ''}
                    ${isLocked && config.day !== 1 ? 'opacity-60' : 'hover:border-[#d4af37]/30'}
                  `}
                  data-testid={`day-${config.day}-card`}
                >
                  {/* Day Number */}
                  <div className={`
                    w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0
                    ${isCompleted ? 'bg-[#d4af37]/20' : isCurrent ? 'bg-[#d4af37]/10 border border-[#d4af37]' : 'bg-zinc-800'}
                  `}>
                    {isLocked && config.day !== 1 ? (
                      <Lock className="w-6 h-6 text-zinc-600" />
                    ) : isCompleted ? (
                      <CheckCircle2 className="w-6 h-6 text-[#d4af37]" />
                    ) : (
                      <span className={`text-xl font-medium ${isCurrent || config.day === 1 ? 'text-[#d4af37]' : 'text-zinc-500'}`}>
                        {config.day}
                      </span>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="text-[#d4af37] text-xs uppercase tracking-widest">
                        Day {config.day} · {config.phase}
                      </span>
                      <span className="text-zinc-600 text-xs">{config.time}</span>
                      {config.free && (
                        <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-medium">
                          FREE
                        </span>
                      )}
                      {isCurrent && (
                        <span className="text-xs bg-[#d4af37] text-black px-2 py-0.5 rounded-full font-medium">
                          Current
                        </span>
                      )}
                    </div>
                    <h3 className="text-white font-medium mb-1">{config.title}</h3>
                    <p className="text-zinc-500 text-sm truncate">{config.description}</p>
                  </div>
                  
                  {/* Arrow */}
                  {(!isLocked || config.day === 1) && (
                    <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                  )}
                </Card>
              );
            })}
          </div>
        </div>

        {/* Scripture Card */}
        <Card className="bg-[#1f1f1f] border-zinc-800 p-8 text-center" data-testid="scripture-card">
          <p className="text-zinc-400 italic text-lg mb-4 leading-relaxed">
            "If any of you lacks wisdom, let him ask God, who gives generously 
            to all without reproach, and it will be given him."
          </p>
          <p className="text-[#d4af37] text-sm">James 1:5</p>
        </Card>
      </main>
    </div>
  );
};

export default Dashboard;
